import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { Entity, EntityRecord, RecordGroup } from '#types/System.types';
import { useGetEntityGroupRecords } from './useGetEntityGroupRecords';
import { createCollectionStore } from '../store';

interface SelectOption {
    label: string;
    value: string;
    description?: string;
}

interface GroupOption {
    label: string;
    options: SelectOption[];
}

interface EntityGroupSelection {
    entityId: Entity['docId'];
    entityGroupId: RecordGroup['entityGroupId'] | null;
}

interface UseSelectEntityGroupProps {
    limit?: number;
}

interface UseSelectEntityGroupReturn {
    selection: EntityGroupSelection | null;
    setSelection: (value: EntityGroupSelection | null) => void;
    entities: Entity[] | null;
    loading: boolean;
    groupOptions: GroupOption[];
    records: EntityRecord[] | null;
    recordsLoading: boolean;
}

const entitiesStore = createCollectionStore<Entity>('entities');

export const useGetEntityGroups = ({
    limit: recordLimit = 30
}: UseSelectEntityGroupProps = {}): UseSelectEntityGroupReturn => {
    const [selection, setSelection] = useState<EntityGroupSelection | null>(null);
    const [records, setRecords] = useState<EntityRecord[] | null>(null);
    const [recordsLoading, setRecordsLoading] = useState(false);

    useEffect(() => {
        entitiesStore.connect();
        return () => entitiesStore.disconnect();
    }, []);

    const snap = useSnapshot(entitiesStore.state);
    const entities = useMemo<Entity[]>(() => {
        const result: Entity[] = [];
        for (const id of snap.docIds) {
            const entry = snap.docs.get(id);
            if (entry) result.push({ ...(entry.data as Entity), docId: id });
        }
        return result;
    }, [snap.docIds, snap.docs]);

    const fetchRecords = useGetEntityGroupRecords({ entities, recordLimit });

    const getGroupOptions = useCallback((entitiesList: Entity[] | null): GroupOption[] => {
        if (!entitiesList) return [];

        const entityOptions = entitiesList
            .map(entity => ({
                label: `${entity?.description?.shortLabel || entity?.description?.longLabel || entity.docId || ''}`,
                value: JSON.stringify({ entityId: entity.docId, entityGroupId: null }),
                description: entity.description?.shortDescription
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        const groupOptionsByEntity = entitiesList
            .sort((a, b) => (a.description?.shortLabel || '').localeCompare(b.description?.shortLabel || ''))
            .reduce<GroupOption[]>((acc, entity) => {
                const entityGroups = Object.entries(entity.recordGroups || {})
                    .map(([entityGroupId, group]) => ({
                        label: group?.description?.shortLabel || entityGroupId,
                        value: JSON.stringify({ entityId: entity.docId, entityGroupId }),
                        description: group.description?.shortDescription
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                if (entityGroups.length === 0) return acc;

                acc.push({
                    label: `${entity.description?.shortLabel || entity.docId} Groups`,
                    options: entityGroups
                });
                return acc;
            }, []);

        return [
            {
                label: 'Entities',
                options: entityOptions
            },
            ...groupOptionsByEntity
        ];
    }, []);

    useEffect(() => {
        if (selection) {
            setRecordsLoading(true);
            fetchRecords(selection)
                .then(fetchedRecords => {
                    setRecords(fetchedRecords);
                })
                .catch(error => {
                    console.error('Error fetching records:', error);
                    setRecords(null);
                })
                .finally(() => {
                    setRecordsLoading(false);
                });
        } else {
            setRecords(null);
        }
    }, [selection, fetchRecords]);

    return {
        selection,
        setSelection,
        entities,
        loading: snap.loading,
        groupOptions: getGroupOptions(entities),
        records,
        recordsLoading
    };
};
